import React, { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { ManualLoader } from "src/components";
import { useMediaQuery } from "@mui/material";
import { securityOptions } from "src/shared/config/storeConfig";
import {
  clearAuthReturnTo,
  stashAuthReturnTo,
} from "src/shared/utilities/authRedirect";

/** Rent paths that can add methods or execute a charge — blocked under login-as. */
function isLoginAsBlockedPaymentPath(pathname: string): boolean {
  if (!securityOptions.getLoginAsToken()) return false;
  return (
    pathname === "/rent/payment" ||
    pathname.startsWith("/rent/create/") ||
    pathname === "/rent/linked-accounts" ||
    pathname.startsWith("/rent/linked-accounts/")
  );
}

/**
 * Gate for authenticated routes. With Auth0 cacheLocation='memory' the token
 * cache is wiped on every reload / new tab, so isAuthenticated is false until
 * we re-acquire tokens. Silent-auth-via-iframe is blocked by third-party-
 * cookie restrictions when the app and Auth0 tenant aren't on the same
 * domain (no custom domain set up), so iframe SSO doesn't kick in.
 *
 * Instead, when an unauthenticated user lands on a protected route we
 * trigger a full-page loginWithRedirect with the current path as returnTo.
 * Auth0 sees the valid SSO cookie (first-party in a top-level navigation),
 * auto-approves without prompting, and redirects back to the page — so the
 * user sees a brief redirect flash but never a login screen / login button.
 *
 * Ops "Login as user" / correction grants bypass Auth0: the deep-link token
 * is already the API bearer (see storeConfig). Login-as cannot open payment
 * routes (pay / add card / add bank) — backend also rejects those APIs.
 *
 * Once an Auth0 Custom Domain is wired up, silent auth will work as a
 * first-party iframe and this redirect becomes unnecessary.
 */
const PrivateRoute = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const matches = useMediaQuery("(min-width: 768px)");
  const location = useLocation();
  // Guards against firing loginWithRedirect more than once while the
  // browser is navigating away.
  const redirectingRef = useRef(false);
  const hasOpsGrant = securityOptions.hasOpsGrant();

  useEffect(() => {
    if (hasOpsGrant) return;
    if (isLoading) return;
    if (isAuthenticated) {
      clearAuthReturnTo();
      redirectingRef.current = false;
      return;
    }
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    const returnTo = location.pathname + location.search;
    stashAuthReturnTo(returnTo);
    loginWithRedirect({
      authorizationParams: {
        responseType: "code",
        redirectUri: `${window.location.origin}`,
        mode: "login",
      },
      appState: { returnTo },
    });
  }, [
    hasOpsGrant,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    location.pathname,
    location.search,
  ]);

  if (isAuthenticated && !matches) {
    // @ts-ignore
    window["ZohoHCAsap"]?.Action?.("hideLauncher");
  }
  if (matches) {
    // @ts-ignore
    window["ZohoHCAsap"]?.Action?.("showLauncher");
  }

  if (isLoginAsBlockedPaymentPath(location.pathname)) {
    return <Navigate to="/home" replace />;
  }

  const allowed = hasOpsGrant || (isAuthenticated && !isLoading);
  return <>{allowed ? <Outlet /> : <ManualLoader />}</>;
};

export default PrivateRoute;
